<template>
  <div class="gallery-manager">
    <div class="manager-header">
      <h3>Manage Galleries</h3>
      <n-button type="primary" @click="showCreateDialog = true">
        <template #icon>
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
          </svg>
        </template>
        Create Gallery
      </n-button>
    </div>

    <n-list v-if="galleries.length > 0" class="gallery-list">
      <n-list-item v-for="gallery in galleries" :key="gallery.id">
        <n-thing>
          <template #header>
            <div class="gallery-item-header">
              <span class="gallery-name">{{ gallery.name }}</span>
              <n-tag v-if="gallery.isDefault" type="info" size="small">Default</n-tag>
              <n-tag v-if="(gallery as any).galleryType === 'type-library'" type="success" size="small">Type Library</n-tag>
              <n-tag v-if="gallery.isPrivate" type="warning" size="small">Private</n-tag>
              <n-tag :type="getAccessLevelTagType(gallery.accessLevel)" size="small">
                {{ gallery.accessLevel }}
              </n-tag>
            </div>
          </template>
          <template #description>
            <div class="gallery-meta">
              <span v-if="gallery.description">{{ gallery.description }}</span>
              <span v-else class="text-muted">No description</span>
              <span class="gallery-image-count">{{ gallery.imageCount || 0 }} images</span>
            </div>
          </template>
          <template #action>
            <div class="gallery-actions">
              <n-button size="small" @click="editGallery(gallery)">Edit</n-button>
              <n-button size="small" @click="editPermissions(gallery)">Permissions</n-button>
              <n-button
                v-if="gallery.accessLevel === 'ADMIN'"
                size="small"
                type="error"
                @click="confirmDelete(gallery)"
              >
                Delete
              </n-button>
            </div>
          </template>
        </n-thing>
      </n-list-item>
    </n-list>

    <n-empty v-else description="No galleries yet">
      <template #extra>
        <n-button type="primary" @click="showCreateDialog = true">Create Gallery</n-button>
      </template>
    </n-empty>

    <!-- Create/Edit Gallery Dialog -->
    <n-modal v-model:show="showCreateDialog" preset="card" :title="editingGallery ? 'Edit Gallery' : 'Create Gallery'" style="width: 600px">
      <n-form ref="galleryFormRef" :model="galleryForm" :rules="galleryFormRules">
        <n-form-item label="Name" path="name">
          <n-input v-model:value="galleryForm.name" placeholder="Gallery name" />
        </n-form-item>
        <n-form-item label="Description" path="description">
          <n-input
            v-model:value="galleryForm.description"
            type="textarea"
            placeholder="Optional description"
            :rows="3"
          />
        </n-form-item>
        <n-form-item v-if="!editingGallery" label="Gallery Type">
          <n-select
            v-model:value="galleryForm.galleryType"
            :options="[{ label: 'Image Gallery', value: 'images' }, { label: 'Type Library (Fonts)', value: 'type-library' }]"
          />
        </n-form-item>
        <n-form-item label="Visibility">
          <n-switch v-model:value="galleryForm.isPrivate" :checked-value="false" :unchecked-value="true">
            <template #checked>Public</template>
            <template #unchecked>Private</template>
          </n-switch>
        </n-form-item>
      </n-form>
      <template #action>
        <div style="display: flex; gap: 8px; justify-content: flex-end; width: 100%;">
          <n-button @click="showCreateDialog = false">Cancel</n-button>
          <n-button type="primary" @click="saveGallery" :loading="saving">
            {{ editingGallery ? 'Update' : 'Create' }}
          </n-button>
        </div>
      </template>
    </n-modal>

    <!-- Permissions Editor Dialog -->
    <n-modal v-model:show="showPermissionsDialog" preset="card" title="Gallery Permissions" style="width: 700px">
      <GalleryPermissionEditor
        v-if="editingPermissionsGallery"
        :gallery="editingPermissionsGallery"
        @save="handlePermissionsSave"
        @cancel="showPermissionsDialog = false"
      />
    </n-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { NList, NListItem, NThing, NTag, NButton, NModal, NForm, NFormItem, NInput, NSwitch, NEmpty, useMessage, useDialog } from 'naive-ui'
import { apiClient } from '../services/api'
import GalleryPermissionEditor from './GalleryPermissionEditor.vue'

const message = useMessage()
const dialog = useDialog()

interface Gallery {
  id: string
  name: string
  description?: string
  ownerBrandId?: string
  isPrivate: boolean
  isDefault: boolean
  permissions: any[]
  accessLevel: 'READ' | 'WRITE' | 'ADMIN'
  imageCount?: number
}

const props = defineProps<{
  brandId?: string
}>()

const emit = defineEmits<{
  (e: 'gallery-updated'): void
}>()

const galleries = ref<Gallery[]>([])
const loading = ref(false)
const saving = ref(false)
const showCreateDialog = ref(false)
const showPermissionsDialog = ref(false)
const editingGallery = ref<Gallery | null>(null)
const editingPermissionsGallery = ref<Gallery | null>(null)
const galleryFormRef = ref()

const galleryForm = ref({
  name: '',
  description: '',
  isPrivate: true,
  galleryType: 'images' as 'images' | 'type-library',
  ownerBrandId: undefined as string | undefined,
})

const galleryFormRules = {
  name: {
    required: true,
    message: 'Please enter a gallery name',
    trigger: 'blur',
  },
}

const getActiveBrandId = (): string | undefined => props.brandId || localStorage.getItem('selectedBrandId') || undefined

const getAccessLevelTagType = (level: 'READ' | 'WRITE' | 'ADMIN') => {
  switch (level) {
    case 'READ':
      return 'default'
    case 'WRITE':
      return 'warning'
    case 'ADMIN':
      return 'success'
    default:
      return 'default'
  }
}

const loadGalleries = async () => {
  loading.value = true
  try {
    const response = await apiClient.fetchGalleries(getActiveBrandId())
    galleries.value = response.galleries
  } catch (error: any) {
    message.error(`Failed to load galleries: ${error.message || 'Unknown error'}`)
  } finally {
    loading.value = false
  }
}

const editGallery = (gallery: Gallery) => {
  editingGallery.value = gallery
  galleryForm.value = {
    name: gallery.name,
    description: gallery.description || '',
    isPrivate: gallery.isPrivate,
    galleryType: 'images',
    ownerBrandId: gallery.ownerBrandId,
  }
  showCreateDialog.value = true
}

const editPermissions = (gallery: Gallery) => {
  editingPermissionsGallery.value = gallery
  showPermissionsDialog.value = true
}

const saveGallery = async () => {
  try {
    await galleryFormRef.value?.validate()
    saving.value = true
    const activeBrandId = getActiveBrandId()

    if (editingGallery.value) {
      await apiClient.updateGallery(editingGallery.value.id, galleryForm.value)
      message.success('Gallery updated successfully')
    } else {
      if (!activeBrandId) {
        message.error('Select a company before creating galleries')
        return
      }
      await apiClient.createGallery({
        ...galleryForm.value,
        ownerBrandId: activeBrandId,
      })
      message.success('Gallery created successfully')
    }

    showCreateDialog.value = false
    editingGallery.value = null
    galleryForm.value = {
      name: '',
      description: '',
      isPrivate: true,
      galleryType: 'images',
      ownerBrandId: undefined,
    }
    await loadGalleries()
    emit('gallery-updated')
  } catch (error: any) {
    if (error.errors) {
      // Validation errors
      return
    }
    message.error(`Failed to save gallery: ${error.message || 'Unknown error'}`)
  } finally {
    saving.value = false
  }
}

const handlePermissionsSave = async () => {
  showPermissionsDialog.value = false
  editingPermissionsGallery.value = null
  await loadGalleries()
  emit('gallery-updated')
}

const confirmDelete = (gallery: Gallery) => {
  dialog.warning({
    title: 'Delete Gallery',
    content: `Are you sure you want to delete "${gallery.name}"? This will remove all images from this gallery but will not delete the images themselves.`,
    positiveText: 'Delete',
    negativeText: 'Cancel',
    onPositiveClick: async () => {
      try {
        await apiClient.deleteGallery(gallery.id)
        message.success('Gallery deleted successfully')
        await loadGalleries()
        emit('gallery-updated')
      } catch (error: any) {
        message.error(`Failed to delete gallery: ${error.message || 'Unknown error'}`)
      }
    },
  })
}

onMounted(async () => {
  await loadGalleries()
})
</script>

<style scoped>
.gallery-manager {
  padding: 16px;
}

.manager-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.manager-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.gallery-list {
  margin-top: 16px;
}

.gallery-item-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gallery-name {
  font-weight: 600;
  font-size: 16px;
}

.gallery-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color-secondary);
  font-size: 14px;
}

.text-muted {
  color: var(--text-color-disabled);
  font-style: italic;
}

.gallery-image-count {
  font-weight: 500;
}

.gallery-actions {
  display: flex;
  gap: 8px;
}
</style>
